import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const CriticalCareTechnologyCoursePage: React.FC = () => {
  const criticalCareData = {
    title: "Bachelor of Science in Critical Care Technology",
    shortName: "B.Sc CCT",
    duration: "3 Years",
    description: "A specialized healthcare program focusing on intensive care unit operations, life support systems, critical care monitoring, and emergency medical technology.",
    overview: "B.Sc Critical Care Technology prepares students to become critical care technologists who work in intensive care units, emergency departments, and other critical care settings. The program covers advanced life support, critical care monitoring, ventilator management, emergency procedures, and patient assessment in life-threatening situations. Students learn to operate sophisticated medical equipment, assist in emergency procedures, and provide specialized care for critically ill patients.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in emergency medicine and critical care",
      "Ability to work under high stress",
      "Good physical and mental stamina"
    ],
    careerProspects: {
      roles: [
        "Critical Care Technologist",
        "ICU Technician",
        "Emergency Medical Technician",
        "Life Support Specialist",
        "Ventilator Technician",
        "Critical Care Monitor Specialist",
        "Emergency Room Technician",
        "Trauma Care Specialist",
        "Intensive Care Coordinator",
        "Critical Care Equipment Specialist"
      ],
      industries: [
        "Intensive Care Units",
        "Emergency Departments",
        "Trauma Centers",
        "Cardiac Care Units",
        "Neonatal ICUs",
        "Surgical ICUs",
        "Emergency Medical Services",
        "Air Ambulance Services",
        "Medical Equipment Companies",
        "Critical Care Transport"
      ],
      averageSalary: "₹3.5-8 LPA (varies by experience and location)",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "AIIMS Emergency",
        "Medanta Hospital",
        "Manipal Hospitals",
        "Emergency Medical Services",
        "Narayana Health",
        "Government Hospitals",
        "Medical Equipment Companies"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy and Physiology",
          "Medical Emergency Fundamentals",
          "Basic Life Support",
          "Medical Terminology",
          "Pathophysiology",
          "Patient Assessment",
          "Communication Skills",
          "Introduction to Critical Care"
        ]
      },
      {
        year: 2,
        subjects: [
          "Advanced Life Support",
          "Ventilator Management",
          "Critical Care Monitoring",
          "Emergency Medications",
          "Trauma Care",
          "Cardiac Emergency Care",
          "Equipment Operation",
          "Infection Control"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Critical Care Procedures",
          "Pediatric and Neonatal Critical Care",
          "Emergency Response Systems",
          "Quality Management",
          "Leadership in Emergency Care",
          "Research Methods",
          "Professional Practice",
          "Clinical Internship"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Critical Care Technology",
      "Entrance examination (typically NEET-based)",
      "Merit-based selection",
      "Physical fitness assessment",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Intensive training orientation"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹20,000 PA",
        ranking: "Premier Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3.5 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹3 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Government Medical College",
        location: "Mumbai, Maharashtra",
        fees: "₹50,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Emergency Response",
      "Equipment Operation",
      "Quick Decision Making",
      "Stress Management",
      "Patient Care",
      "Technical Proficiency",
      "Communication Skills",
      "Teamwork",
      "Attention to Detail",
      "Physical Stamina",
      "Problem Solving",
      "Continuous Learning"
    ],
    futureScope: "Critical care technology is advancing with artificial intelligence in patient monitoring, robotic assistance in ICUs, telemedicine for remote critical care consultation, and advanced life support technologies. Future opportunities include specialization in pediatric critical care, cardiac critical care, trauma systems, and emerging fields like extracorporeal membrane oxygenation (ECMO) and artificial organ support.",
    faqs: [
      {
        question: "What is the role of a critical care technologist?",
        answer: "Critical care technologists monitor critically ill patients, operate life support equipment, assist with emergency procedures, maintain critical care devices, and provide specialized technical support in ICUs and emergency departments."
      },
      {
        question: "Is this field very stressful?",
        answer: "Yes, critical care involves high-stress situations with life-or-death decisions. However, proper training includes stress management techniques, and the ability to save lives makes it highly rewarding for those suited to emergency medicine."
      },
      {
        question: "What are the working hours like?",
        answer: "Critical care units operate 24/7, so work involves shift work including nights, weekends, and holidays. Emergency situations may require overtime, but this varies by facility and position."
      },
      {
        question: "Can I specialize in specific areas of critical care?",
        answer: "Yes, specializations include pediatric/neonatal ICU, cardiac critical care, trauma, burns, neurocritical care, and specialized procedures like ECMO or continuous renal replacement therapy."
      },
      {
        question: "What certifications enhance career prospects?",
        answer: "Certifications in BLS, ACLS, PALS, trauma care (ATLS), and specialized equipment operation enhance career prospects. Some countries also offer critical care technologist certification programs."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {criticalCareData.title} ({criticalCareData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={criticalCareData} />
      </div>
    </div>
  );
};

export default CriticalCareTechnologyCoursePage;